
import { AIModule } from '../../types';

export const MLOPS_MODULE_1: AIModule = {
  id: 'mlops-foundations',
  title: 'MLOps Foundations',
  description: 'Introduction to MLOps practices and production ML systems.',
  duration: '3 hours',
  difficulty: 'medium',
  content: `
# MLOps Foundations

MLOps (Machine Learning Operations) is a set of practices that aims to deploy and maintain machine learning models in production reliably and efficiently. It combines Machine Learning, DevOps, and Data Engineering practices.

## What is MLOps?

MLOps is the practice of collaboration between Data Scientists and Operations teams to help manage production ML lifecycle. It focuses on:

- Automated deployment of ML models
- Monitoring and maintenance of ML systems
- Continuous integration and delivery for ML
- Reproducibility and version control for ML experiments

## Key MLOps Principles

### 1. Automation
Automate the entire ML pipeline from data ingestion to model deployment.

### 2. Continuous Integration/Continuous Deployment (CI/CD)
Apply software engineering best practices to ML workflows.

### 3. Monitoring and Observability
Track model performance, data drift, and system health in production.

### 4. Reproducibility
Ensure experiments and deployments can be reproduced consistently.

### 5. Collaboration
Enable seamless collaboration between data scientists, engineers, and operations teams.

## MLOps Lifecycle

### 1. Data Management
- Data versioning
- Data validation
- Data pipeline automation

### 2. Model Development
- Experiment tracking
- Model versioning
- Collaborative development

### 3. Model Deployment
- Automated deployment pipelines
- A/B testing
- Canary deployments

### 4. Model Monitoring
- Performance monitoring
- Data drift detection
- Model degradation alerts

## Simple MLOps Pipeline Example

\`\`\`python
import mlflow
import mlflow.sklearn
from sklearn.model_selection import train_test_split
from sklearn.ensemble import RandomForestClassifier
from sklearn.metrics import accuracy_score
import pandas as pd

class MLOpsPipeline:
    def __init__(self, experiment_name="ml_experiment"):
        mlflow.set_experiment(experiment_name)
        self.model = None
    
    def prepare_data(self, data_path):
        """Load and prepare data for training"""
        # In a real scenario, this would include data validation
        data = pd.read_csv(data_path)
        X = data.drop('target', axis=1)
        y = data['target']
        
        return train_test_split(X, y, test_size=0.2, random_state=42)
    
    def train_model(self, X_train, y_train, **params):
        """Train model with experiment tracking"""
        with mlflow.start_run():
            # Log parameters
            mlflow.log_params(params)
            
            # Train model
            self.model = RandomForestClassifier(**params)
            self.model.fit(X_train, y_train)
            
            # Calculate and log metrics
            train_accuracy = self.model.score(X_train, y_train)
            mlflow.log_metric("train_accuracy", train_accuracy)
            
            # Log model
            mlflow.sklearn.log_model(self.model, "model")
            
            return self.model
    
    def evaluate_model(self, X_test, y_test):
        """Evaluate model performance"""
        if self.model is None:
            raise ValueError("Model not trained yet")
        
        predictions = self.model.predict(X_test)
        accuracy = accuracy_score(y_test, predictions)
        
        with mlflow.start_run():
            mlflow.log_metric("test_accuracy", accuracy)
        
        return accuracy
    
    def deploy_model(self, model_name, stage="Staging"):
        """Register and deploy model"""
        # Register model
        model_uri = f"runs:/{mlflow.active_run().info.run_id}/model"
        mlflow.register_model(model_uri, model_name)
        
        # Transition to deployment stage
        client = mlflow.MlflowClient()
        client.transition_model_version_stage(
            name=model_name,
            version=1,
            stage=stage
        )
        
        print(f"Model {model_name} deployed to {stage}")

# Example usage
pipeline = MLOpsPipeline("iris_classification")

# This would typically load real data
# X_train, X_test, y_train, y_test = pipeline.prepare_data("iris.csv")

# Train model with hyperparameters
# model = pipeline.train_model(
#     X_train, y_train, 
#     n_estimators=100, 
#     max_depth=5
# )

# Evaluate model
# accuracy = pipeline.evaluate_model(X_test, y_test)
# print(f"Test accuracy: {accuracy}")

# Deploy model
# pipeline.deploy_model("iris_classifier")
\`\`\`

MLOps is essential for scaling machine learning in production environments and ensuring reliable, maintainable ML systems.
  `,
  quiz: {
    id: 'mlops-foundations-quiz',
    title: 'MLOps Foundations Quiz',
    passThreshold: 70,
    questions: [
      {
        id: 'q1',
        question: 'What is the primary goal of MLOps?',
        type: 'multiple-choice',
        options: [
          'To develop new machine learning algorithms',
          'To deploy and maintain ML models in production reliably',
          'To collect more training data',
          'To improve model accuracy'
        ],
        correctAnswer: 1,
        explanation: 'MLOps focuses on deploying and maintaining machine learning models in production environments reliably and efficiently.',
        difficulty: 'easy',
        points: 10
      }
    ]
  },
  resources: [
    {
      id: 'mlops-video',
      title: 'MLOps Introduction',
      type: 'video',
      url: 'https://example.com/mlops',
      description: 'Introduction to MLOps practices and tools'
    }
  ]
};
